/**
 * Webhook Event Types
 *
 * TypeScript types for Stripe webhook event payloads.
 * Used for simulating webhook events in development.
 */

// ============================================================================
// Event Types
// ============================================================================

export type WebhookEventType =
  | 'checkout.session.completed'
  | 'customer.subscription.created'
  | 'customer.subscription.updated'
  | 'customer.subscription.deleted'
  | 'invoice.paid'
  | 'invoice.payment_failed'
  | 'invoice.payment_action_required'
  | 'customer.created'
  | 'customer.updated'
  | 'payment_method.attached'
  | 'payment_method.detached';

// ============================================================================
// Base Event Interface
// ============================================================================

export interface WebhookEvent<T = unknown> {
  id: string;
  type: WebhookEventType;
  created: number; // Unix timestamp
  data: {
    object: T;
    previous_attributes?: Partial<T>;
  };
  livemode: boolean;
}

// ============================================================================
// Subscription Object
// ============================================================================

export interface StripeSubscription {
  id: string;
  customer: string;
  status: 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing' | 'incomplete' | 'incomplete_expired';
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  ended_at: number | null;
  trial_start: number | null;
  trial_end: number | null;
  items: {
    data: Array<{
      id: string;
      price: {
        id: string;
        product: string;
        unit_amount: number;
        currency: string;
        recurring: {
          interval: 'month' | 'year';
          interval_count: number;
        };
      };
    }>;
  };
  latest_invoice: string | null;
  default_payment_method: string | null;
  metadata: Record<string, string>;
}

// ============================================================================
// Invoice Object
// ============================================================================

export interface StripeInvoice {
  id: string;
  customer: string;
  subscription: string | null;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  amount_due: number;
  amount_paid: number;
  amount_remaining: number;
  currency: string;
  created: number;
  due_date: number | null;
  paid: boolean;
  payment_intent: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  attempt_count: number;
  next_payment_attempt: number | null;
}

// ============================================================================
// Checkout Session Object
// ============================================================================

export interface StripeCheckoutSession {
  id: string;
  customer: string | null;
  subscription: string | null;
  payment_status: 'paid' | 'unpaid' | 'no_payment_required';
  status: 'open' | 'complete' | 'expired';
  mode: 'payment' | 'setup' | 'subscription';
  success_url: string;
  cancel_url: string;
  metadata: Record<string, string>;
}

// ============================================================================
// Customer Object
// ============================================================================

export interface StripeCustomer {
  id: string;
  email: string | null;
  name: string | null;
  created: number;
  default_source: string | null;
  invoice_settings: {
    default_payment_method: string | null;
  };
  metadata: Record<string, string>;
}

// ============================================================================
// Payment Method Object
// ============================================================================

export interface StripePaymentMethod {
  id: string;
  customer: string | null;
  type: 'card' | 'sepa_debit' | 'bank_transfer';
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  created: number;
}

// ============================================================================
// Event Payloads
// ============================================================================

export type CheckoutSessionCompletedEvent = WebhookEvent<StripeCheckoutSession>;
export type SubscriptionCreatedEvent = WebhookEvent<StripeSubscription>;
export type SubscriptionUpdatedEvent = WebhookEvent<StripeSubscription>;
export type SubscriptionDeletedEvent = WebhookEvent<StripeSubscription>;
export type InvoicePaidEvent = WebhookEvent<StripeInvoice>;
export type InvoicePaymentFailedEvent = WebhookEvent<StripeInvoice>;
export type CustomerCreatedEvent = WebhookEvent<StripeCustomer>;
export type PaymentMethodAttachedEvent = WebhookEvent<StripePaymentMethod>;

// ============================================================================
// Type Guards
// ============================================================================

export function isSubscriptionEvent(
  event: WebhookEvent
): event is WebhookEvent<StripeSubscription> {
  return event.type.startsWith('customer.subscription.');
}

export function isInvoiceEvent(
  event: WebhookEvent
): event is WebhookEvent<StripeInvoice> {
  return event.type.startsWith('invoice.');
}

export function isCheckoutEvent(
  event: WebhookEvent
): event is WebhookEvent<StripeCheckoutSession> {
  return event.type.startsWith('checkout.');
}

// ============================================================================
// Event Factory (for testing)
// ============================================================================

export function createMockEvent<T>(
  type: WebhookEventType,
  data: T
): WebhookEvent<T> {
  return {
    id: `evt_mock_${Date.now()}`,
    type,
    created: Math.floor(Date.now() / 1000),
    data: { object: data },
    livemode: false,
  };
}
